import React, { useEffect, useState } from "react";
import "./Template.css";

import TemplateFilter from "./TemplateFilter";
import TemplateTable from "./TemplateTable";
import TemplatePagination from "./TemplatePagination";
import { getDataDetail, getDataList } from "../../../../../api/dataApi";

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}.${mm}.${dd}`;
};

const getCategoryLabel = (value) => {
  if (value === "BUDGET" || value === "ACCOUNTING") return "회계";
  if (value === "EVENT") return "행사/사업";
  if (value === "ADMINISTRATION") return "행정/운영";
  if (value === "ETC") return "기타";
  if (value === "회계") return "회계";
  if (value === "행사/사업") return "행사/사업";
  if (value === "행정/운영") return "행정/운영";
  if (value === "기타") return "기타";

  return "";
};

const normalizeItem = (item) => {
  const rawCategory = item.category || item.docCategory || item.listCategory;
  const category = getCategoryLabel(rawCategory);

  return {
    id:
      item.id ||
      item.docId ||
      item.councilDocId ||
      item.councilFormId ||
      item.councilDocumentId,
    category,
    title: item.title || "",
    writer: item.writer || item.userName || item.createdBy || "총학생회",
    date: formatDate(item.date || item.createdAt || item.updatedAt),
    rawDate: item.date || item.createdAt || item.updatedAt || "",
    view: item.viewer ?? item.view ?? 0,
    content:
      item.content ||
      item.contents ||
      item.description ||
      item.body ||
      "",
  };
};

const getPagedArray = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.content)) return result.content;
  if (Array.isArray(result?.items)) return result.items;
  if (Array.isArray(result?.list)) return result.list;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.data?.content)) return result.data.content;

  return [];
};

const sortLatestFirst = (a, b) => {
  const dateA = new Date(a.rawDate).getTime();
  const dateB = new Date(b.rawDate).getTime();

  if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) {
    return dateB - dateA;
  }

  return Number(b.id || 0) - Number(a.id || 0);
};

const getDetailContent = async (id) => {
  if (!id) return "";

  try {
    const detail = await getDataDetail("stdcouncil", id);

    return (
      detail?.content ||
      detail?.contents ||
      detail?.description ||
      detail?.body ||
      ""
    );
  } catch (error) {
    console.error("총학생회 양식 상세 내용 조회 실패:", error);
    return "";
  }
};

export default function Template() {
  const [category, setCategory] = useState("전체");
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("title");
  const [page, setPage] = useState(1);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);

  const loadMissingContents = async () => {
    const hasMissingContent = data.some((item) => item.id && !item.content);

    if (!hasMissingContent) return;

    try {
      setContentLoading(true);

      const nextData = await Promise.all(
        data.map(async (item) => {
          if (!item.id || item.content) return item;

          const detailContent = await getDetailContent(item.id);

          return {
            ...item,
            content: detailContent,
          };
        })
      );

      setData(nextData);
    } finally {
      setContentLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchType !== "title") {
      await loadMissingContents();
    }

    setSearchQuery(search);
    setPage(1);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);

        const result = await getDataList({
          category: "stdcouncil",
          activeDataType: "form",
          dataFilterType: "all",
          searchCategory: "TITLE",
          keyWord: "",
          page: 0,
          size: 100,
        });

        const list = getPagedArray(result)
          .map(normalizeItem)
          .sort(sortLatestFirst);

        setData(list);
      } catch (error) {
        console.error("총학생회 양식 목록 조회 실패:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchType !== "title") {
        await loadMissingContents();
      }

      setSearchQuery(search);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, searchType]);

  useEffect(() => {
    setPage(1);
  }, [category]);

  const filteredData = data.filter((item) => {
    const keyword = searchQuery.trim();
    const matchCategory = category === "전체" || item.category === category;

    const matchSearch = !keyword
      ? true
      : searchType === "title"
        ? (item.title || "").includes(keyword)
        : (item.content || "").includes(keyword);

    return matchCategory && matchSearch;
  });

  const itemsPerPage = 10;
  const total = filteredData.length;
  const totalPages = Math.ceil(total / itemsPerPage);
  const safePage = total === 0 ? 1 : Math.min(page, totalPages || 1);

  useEffect(() => {
    if (total > 0 && page > totalPages) {
      setPage(1);
    }
  }, [total, page, totalPages]);

  const pagedData = filteredData.slice(
    (safePage - 1) * itemsPerPage,
    safePage * itemsPerPage
  );

  return (
    <div className="stdcouncil-template">
      <div className="stdcouncil-template-inner">
        <div className="stdcouncil-template-header">
          학생자치활동에 필요한 각종 양식을 제공합니다.
        </div>

        <TemplateFilter
          category={category}
          setCategory={setCategory}
          search={search}
          setSearch={setSearch}
          searchType={searchType}
          setSearchType={setSearchType}
          handleSearch={handleSearch}
        />

        <div className="stdcouncil-template-layout">
          <div className="stdcouncil-template-content">
            {loading || contentLoading ? (
              <div className="stdcouncil-template-empty">
                불러오는 중입니다.
              </div>
            ) : (
              <TemplateTable
                data={pagedData}
                page={safePage}
                itemsPerPage={itemsPerPage}
                total={total}
              />
            )}
          </div>

          {total > 0 && (
            <TemplatePagination
              page={safePage}
              setPage={setPage}
              total={total}
              itemsPerPage={itemsPerPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}